import {cn} from "@/lib/utils";
import {Link} from '@/i18n/navigation';
import {RightMask} from "@/app/components/common/right-mask/RightMask";
import React, {JSX, useLayoutEffect, useRef} from "react";
import {useSnapCarousel} from "react-snap-carousel";
import PageTitle from "@/app/components/common/page-title/PageTitle";
import {CtaBtn} from "@/app/components/common/cta-btn/CtaBtn";
import {AppSlider} from "@/app/components/common/app-slider/AppSlider";
import styles from './snap-carousel.module.css'
import {useTranslations} from "next-intl";

interface IProps<T> {
    href: string;
    title: string;
    icon: JSX.Element;
    items: T[]
    children: (item: T) => JSX.Element;
}

export const SnapCarousel = <T, >({href, title, icon, items, children}: IProps<T>) => {
    const t = useTranslations();
    const {
        scrollRef: slotsScrollRef,
        hasPrevPage: slotsHasPrevPage,
        hasNextPage: slotsHasNextPage,
        prev: slotsPrev,
        next: slotsNext,
        snapPointIndexes: slotsSnapPointIndexes,
        refresh,
    } = useSnapCarousel();

    const syncedItemsLengthRef = useRef(items.length);

    // re-sync pages whenever the child count changes (e.g. skeleton→real data),
    // because the library doesn't watch DOM children and scroll events can fire
    // with stale page indices that exceed the new child count (ALDEX-CASINO-4X).
    // Gated on the count itself: `refresh` gets a new identity every time the
    // library re-attaches `scrollRef`, and the library already refreshes on that
    // itself — re-running here too put a synchronous measure→setState on those
    // commits, on every lobby row (AC-886).
    useLayoutEffect(() => {
        if (syncedItemsLengthRef.current === items.length) return;
        syncedItemsLengthRef.current = items.length;
        refresh();
    }, [items.length, refresh]);

    return (
        <>
            <div className={'flex items-center justify-between'}>
                <Link prefetch={false} href={href}>
                    <PageTitle title={title} icon={icon}/>
                </Link>
                <div className={'flex items-center gap-[8px]'}>
                    <Link prefetch={false} href={href}>
                        <CtaBtn className={'h-[32px] md:h-[41px] py-0'}>{t('SnapCarousel.viewAll')}</CtaBtn>
                    </Link>
                    <AppSlider className={'md:h-[41px] md:w-[82px]'} prevDisabled={!slotsHasPrevPage}
                               nextDisabled={!slotsHasNextPage} onNext={() => slotsNext()}
                               onPrev={() => slotsPrev()}/>
                </div>
            </div>

            <div className={'relative mb-[16px]'}>
                <div className={cn('flex gap-[0.5rem] lg:gap-[1rem] overflow-y-hidden overflow-x-auto pt-[16px] md:pt-[24px]', styles.Container)} ref={slotsScrollRef}>
                    {items.map((item, idx) => (
                            <div key={idx} className={cn({['snap-start']: slotsSnapPointIndexes.has(idx)})}>
                                {children(item)}
                            </div>
                        )
                    )}
                </div>
                <RightMask/>
            </div>
        </>
    )
}
